import axios from "axios";
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Col, Form, Row, Button, Alert } from "react-bootstrap";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import styles from "./forms.module.css";
import loginImg from "../../assets/img/sections/signIn_logo.png";
import { registerAPI } from "../../services/authService";
import { StatusCircle } from "../../components/general/StatusCircle";
import API_URLS from "../../constants/apiConstants";
import { LOGIN_ROUTE } from "../../routes/RoutesPathes";
import PhoneField from "../../components/general/PhoneField"; 

export const Signup = () => {
  const { t, i18n } = useTranslation();
  const arrow = i18n.dir() === 'rtl' ? '←' : '→';
  const [step, setStep] = useState(1);
  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [countries, setCountries] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    password2: "",

    schoolName: "",
    phone: "",
    capacity: "",
    countries: "",
    programType: "",
  });

  const errRef = useRef();

  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const emailRef = useRef();
  const passwordRef = useRef();
  const password2Ref = useRef();
  const schoolNameRef = useRef();
  const capacityRef = useRef();
  const countriesRef = useRef();
  const programTypeRef = useRef();
  const phoneRef = useRef();

  // Mapping of field names to their refs
  const fieldRefs = {
    firstName: firstNameRef,
    lastName: lastNameRef,
    email: emailRef,
    password: passwordRef,
    password2: password2Ref,
    schoolName: schoolNameRef,
    capacity: capacityRef,
    countries: countriesRef,
    programType: programTypeRef,
    phone: phoneRef
  };

  // Focus on email input
  useEffect(() => {
    if (step === 1 || step === 3) {
      firstNameRef.current?.focus();
    } else if (step === 2) {
      schoolNameRef.current?.focus();
    }
  }, [step]);

  // Fetch countries
  useEffect(() => {
    const getCountries = async () => {
      try {
        const response = await axios.get(API_URLS.country);
        if (response.status === 200) {
          setCountries(response.data);
        }
      } catch (err) {
        // Do nothing
      }
    };
    getCountries();
  }, []);

  // Fetch prgram types
  useEffect(() => {
    const getProgramTypes = async () => {
      try {
        const response = await axios.get(API_URLS.programType);
        if (response?.status === 200) {
          setProgramTypes(response?.data);
        }
      } catch (err) {
        // Do nothing
      }
    };
    getProgramTypes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => ({ ...prev, [name]: ""}));
    setErrMsg("");
  };

  const validateStepOne = () => {
    const { firstName, lastName, email, password, password2 } = userData;

    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^(?!\s+$).{1,15}$/; // Not just spaces, 1-15 chars

    // First name validation
    if (!firstName) {
      errors.firstName = "First name is required.";
    } else if (!nameRegex.test(firstName)) {
      errors.firstName = "First name must be 1-15 characters and not just spaces.";
    }

    // Last name validation
    if (!lastName) {
      errors.lastName = "Last name is required.";
    } else if (!nameRegex.test(lastName)) {
      errors.lastName = "Last name must be 1-15 characters and not just spaces.";
    }

    // Email validation
    if (!email) errors.email = "Email is required.";
    else if (!emailRegex.test(email)) errors.email = "Enter a valid email address.";

    // Password validation
    if (!password) errors.password = "Password is required.";
    else if (password.length < 8) errors.password = "Password must be at least 8 characters.";

    // Password2 validation
    if (!password2) errors.password2 = "Confirm your password.";
    else if (password && password2 !== password) errors.password2 = "Passwords do not match.";

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      errRef.current?.focus(); // For accessibility
      return false;
    }

    return true;
  };

  const validateStepTwo = () => {
    const { schoolName, capacity, countries, programType, phone } = userData;

    const errors = {};
    const phoneRegex = /^\+?\d{7,15}$/;

    if (!schoolName) errors.schoolName = "School name is required.";

    if (!capacity || !Number.isInteger(Number(capacity)) || Number(capacity) <= 0) {
      errors.capacity = "Capacity must be a positive integer.";
    }

    if (!countries) errors.countries = "Countries field is required.";
    if (!programType) errors.programType = "Program type is required.";

    if (!phone) errors.phone = "Phone number is required.";
    else if (!phoneRegex.test(phone)) errors.phone = "Enter a valid phone number.";

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      errRef.current?.focus(); // For accessibility
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStepOne()) {
      setStep(2);
    } else if (step === 2 && validateStepTwo()) {
      setStep(3);
    }
    setShowPassword(false);
    setShowPassword2(false);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStepOne() || !validateStepTwo()) {
      // Find and scroll to the first error
      scrollToFirstError();
      return;
    }

    setLoading(true);
    try {
      const response = await registerAPI(userData);
      if (response.status === 201) {
        setSuccess(true);
      }
    } catch (err) {
      if (!err?.response) {
        setErrMsg("No Server Response");
      } else if (err.response?.status === 400) {
        const errors = {};
        if (err.response.data?.email?.[0]) {
          errors.email = err.response.data.email?.[0];
        } else if (err.response.data?.first_name?.[0]) {
          errors.firstName = err.response.data.first_name?.[0];
        } else if (err.response.data?.last_name?.[0]) {
          errors.lastName = err.response.data.last_name?.[0];
        } else if (err.response.data?.password?.[0]) {
          errors.password = err.response.data.password?.[0];
        } else if (err.response.data?.password2?.[0]) {
          errors.password2 = err.response.data.password2?.[0];
        } else if (err.response.data?.name?.[0]) { // schoolName
          errors.name = err.response.data.name?.[0];
        } else if (err.response.data?.phone?.[0]) {
          errors.phone = err.response.data.phone?.[0];
        } else if (err.response.data?.capacity?.[0]) {
          errors.capacity = err.response.data.capacity?.[0];
        } else if (err.response.data?.countries?.[0]) {
          errors.countries = err.response.data.countries?.[0];
        } else if (err.response.data?.program_type?.[0]) {
          errors.programType = err.response.data.program_type?.[0];
        }

        setFieldErrors(errors);
        scrollToFirstError(); // Scroll to first error after API validation fails

      } else {
        setErrMsg("Register failed");
      }
      errRef.current?.focus();
    } finally {
      setLoading(false); // Reset loading regardless of success/failure
    }
  };

  const scrollToFirstError = () => {
    const firstErrorField = Object.keys(fieldErrors).find(field => fieldErrors[field]);
    
    if (firstErrorField && fieldRefs[firstErrorField]?.current) {
      const container = document.getElementById('form-scroll-container');
      const errorElement = fieldRefs[firstErrorField].current;
      
      if (container && errorElement) {
        // Calculate position relative to container
        const containerRect = container.getBoundingClientRect();
        const elementRect = errorElement.getBoundingClientRect();
        const scrollPosition = elementRect.top - containerRect.top + container.scrollTop - 100; // 100px offset
        
        container.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      }
      
      errorElement.focus();
    }
  };

  const renderSuccessRegistration = () => {
    return (
      <div className="container h-100 text-center d-flex align-items-center justify-content-center">
        <div>
          <div className="mb-4 d-flex align-items-center justify-content-center gap-2">
            <StatusCircle status='dark-success' width='28px' height='28px' fontSize='1.1rem' />
            <h1 className="mb-1 d-inline-blok">{t("forms.signup_form.email_verification.sucsses_title")}</h1>
          </div>
          <p className="text-muted mb-3">{t("forms.signup_form.email_verification.sucsses_body")}</p>

          <p className="text-muted">
            {t("forms.signup_form.email_verification.sent_to")}:{" "}
            <strong>{userData.email}</strong>
          </p>
          <p className="text-muted small mb-4">
            {t("forms.signup_form.email_verification.check_email_note")}
          </p>
          <Link to={LOGIN_ROUTE} className="s-link s-purple-link">{t("buttons.login")} {arrow}</Link>
        </div>
      </div>
    )
  };

  const renderStepOneForm = () => {
    return (
      <>
        <Row className="m-0 mb-2">
          <Col md={6}>
            <Form.Group as={Col}>
              <Form.Label className={styles.formLabel}>{t("forms.signup_form.first_name")}</Form.Label>
              <Form.Control
                type="text"
                name="firstName"
                ref={firstNameRef}
                value={userData.firstName}
                onChange={handleChange}
                className={styles.formInput}
                isInvalid={!!fieldErrors.firstName}
                maxLength={50}
                required
              />
              {fieldErrors.firstName && (
                <Form.Text className="text-danger">{fieldErrors.firstName}</Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group as={Col}>
              <Form.Label className={styles.formLabel}>{t("forms.signup_form.last_name")}</Form.Label>
              <Form.Control
                type="text"
                name="lastName"
                ref={lastNameRef}
                value={userData.lastName}
                onChange={handleChange}
                className={styles.formInput}
                isInvalid={!!fieldErrors.lastName}
                maxLength={50}
                required
              />
              {fieldErrors.lastName && (
                <Form.Text className="text-danger">{fieldErrors.lastName}</Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row className="m-0 mb-2">
          <Form.Group as={Col} md="12">
            <Form.Label className={styles.formLabel}>{t("forms.signup_form.email")}</Form.Label>
            <Form.Control
              type="email"
              name="email"
              ref={emailRef}
              value={userData.email}
              onChange={handleChange}
              className={styles.formInput}
              isInvalid={!!fieldErrors.email}
              required
            />
            {fieldErrors.email && (
              <Form.Text className="text-danger">{fieldErrors.email}</Form.Text>
            )}
          </Form.Group>
        </Row>
        <Row className="m-0 mb-2">
          <Form.Group as={Col} md="12" className="position-relative">
            <Form.Label className={styles.formLabel}>{t("forms.signup_form.password1")}</Form.Label>
            <Form.Control
              type={showPassword ? "text" : "password"}
              name="password"
              maxLength={100}
              ref={passwordRef}
              value={userData.password}
              onChange={handleChange}
              className={styles.formInput}
              isInvalid={!!fieldErrors.password}
              required
            />
            <div
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                top: "46px",
                [i18n.dir() === "ltr" ? "right" : "left"]: "25px",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#666",
              }}
            >
              {showPassword ? <BsEyeSlash color="#333" /> : <BsEye color="#333" />}
            </div>
            {fieldErrors.password && (
              <Form.Text className="text-danger">{fieldErrors.password}</Form.Text>
            )}
          </Form.Group>
        </Row>
        <Row className="m-0 mb-2">
          <Form.Group as={Col} md="12" className="position-relative">
            <Form.Label className={styles.formLabel}>{t("forms.signup_form.password2")}</Form.Label>
            <Form.Control
              type={showPassword2 ? "text" : "password"}
              name="password2"
              maxLength={100}
              ref={password2Ref}
              value={userData.password2}
              onChange={handleChange}
              className={styles.formInput}
              isInvalid={!!fieldErrors.password2}
              required
            />
            <div
              onClick={() => setShowPassword2(!showPassword2)}
              style={{
                position: "absolute",
                top: "46px",
                [i18n.dir() === "ltr" ? "right" : "left"]: "25px",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#666",
              }}
            >
              {showPassword2 ? <BsEyeSlash color="#333" /> : <BsEye color="#333" />}
            </div>
            {fieldErrors.password2 && (
              <Form.Text className="text-danger">{fieldErrors.password2}</Form.Text>
            )}
          </Form.Group>
        </Row>
      </>
    );
  };

  const renderStepTwoForm = () => {
    return (
      <>
        <Row className="m-0 mb-2">
          <Form.Group as={Col} md="12">
            <Form.Label className={styles.formLabel}>{t("forms.signup_form.school_name")}</Form.Label>
            <Form.Control
              type="text"
              name="schoolName"
              ref={schoolNameRef}
              value={userData.schoolName}
              onChange={handleChange}
              className={styles.formInput}
              isInvalid={!!fieldErrors.schoolName}
              required
            />
            {fieldErrors.schoolName && (
              <Form.Text className="text-danger">{fieldErrors.schoolName}</Form.Text>
            )}
          </Form.Group>
        </Row>
        <Row className="m-0 mb-2">
          <Form.Group as={Col} md="12">
            <Form.Label className={styles.formLabel}>{t("forms.signup_form.capacity")}</Form.Label>
            <Form.Control
              type="number"
              name="capacity"
              ref={capacityRef}
              value={userData.capacity}
              onChange={handleChange}
              className={styles.formInput}
              min={1} // Minimum value is 1 (positive)
              step={1} // Step of 1 to avoid decimals
              isInvalid={!!fieldErrors.capacity}
              required
            />
            {fieldErrors.capacity && (
              <Form.Text className="text-danger">{fieldErrors.capacity}</Form.Text>
            )}
          </Form.Group>
        </Row>
        <Row className="m-0 mb-2">
          <Form.Group as={Col} md="12">
            <Form.Label className={styles.formLabel}>{t("forms.signup_form.countries")}</Form.Label>
            <Form.Select
              name="countries"
              ref={countriesRef}
              value={userData.countries}
              onChange={handleChange}
              className={styles.formSelect}
              isInvalid={!!fieldErrors.countries}
              required
            >
              <option value="">{t("forms.signup_form.countries")}</option>
              {countries.map((country, index) => (
                <option key={index} value={country.code}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
            {fieldErrors.countries && (
              <Form.Text className="text-danger">{fieldErrors.countries}</Form.Text>
            )}
          </Form.Group>
        </Row>
        <Row className="m-0 mb-2">
          <Form.Group as={Col} md="12">
            <Form.Label className={styles.formLabel}>{t("forms.signup_form.program_type")}</Form.Label>
            <Form.Select
              name="programType"
              value={userData.programType}
              ref={programTypeRef}
              onChange={handleChange}
              className={styles.formSelect}
              isInvalid={!!fieldErrors.programType}
              required
            >
              <option value="">{t("forms.signup_form.program_type")}</option>
              {programTypes?.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name}
                </option>
              ))}
            </Form.Select>
            {fieldErrors.programType && (
              <Form.Text className="text-danger">{fieldErrors.programType}</Form.Text>
            )}
          </Form.Group>
        </Row>
        <Row className="m-0 mb-2">
          <Form.Group as={Col} md="12">
            <PhoneField
              ref={phoneRef}
              label={t("forms.signup_form.phone")}
              value={userData.phone}
              onChange={(val) => {
                setUserData((prev) => ({ ...prev, phone: val }));
                setFieldErrors((prev) => ({ ...prev, phone: "" }));
                setErrMsg("");
              }}
              error={fieldErrors.phone}  
              focusColor="var(--s-yellow-color)"
            />
          </Form.Group>
        </Row>
      </>
    );
  };

  const renderStepOne = () => {
    return (
      <>
        {renderStepOneForm()}
        <Button
          className={`${styles.formBtn} btn-secondary mt-2`}
          style={{width: "100%"}}
          onClick={handleNext}
          type="button"
        >
          {t("buttons.next")}
        </Button>
      </>
    )
  };

  const renderStepTwo = () => {
    return (
      <>
        {renderStepTwoForm()}
        <div className="d-flex justify-content-between">
          <Button className={`${styles.formBtn} btn-secondary mt-2`} onClick={handleBack}>
            {t("buttons.back")}
          </Button>
          <Button
            className={`${styles.formBtn} btn-secondary mt-2`}
            onClick={handleNext}
            type="button"
          >
            {t("buttons.next")}
          </Button>
        </div>
      </>
    )
  };

  const renderStepThree = () => {
    return (
      <>
        <h3 className="mb-4 fw-semibold">
          {t("forms.signup_form.confirmation")}
        </h3>

        <div id="form-scroll-container" style={{height: "20rem", overflow: "auto"}}>
          {renderStepOneForm()}
          {renderStepTwoForm()}
        </div>

        <div className="d-flex justify-content-between">
          <Button className={`${styles.formBtn} btn-secondary mt-2`} onClick={handleBack} disabled={loading}>
            {t("buttons.back")}
          </Button>
          <Button
            className={`${styles.formBtn} btn-secondary mt-2`}
            type="submit"
            disabled={loading}
          >
            {loading ? t("buttons.signup_loading") : t("buttons.signup")}
          </Button>
        </div>
      </>
    )
  };

  const renderSteps = () => {
    return (
      <>
        <div className={styles.progressBar}>
          <div className={`${styles.step} ${step === 1 ? styles.active : ""}`}>1</div>
          <div className={styles.line}></div>
          <div className={`${styles.step} ${step === 2 ? styles.active : ""}`}>2</div>
          <div className={styles.line}></div>
          <div className={`${styles.step} ${step === 3 ? styles.active : ""}`}>3</div>
        </div>

        <Form onSubmit={handleSubmit}>
          {step === 1 && renderStepOne()}

          {step === 2 && renderStepTwo()}

          {step === 3 && renderStepThree()}

          {errMsg && errRef && <Alert variant="danger" className="mt-3 mb-0">
            <p ref={errRef} className="m-0" style={{fontSize: "1rem"}}>{errMsg}</p>
          </Alert>}
        </Form>
      </>
    )
  };

  return (
    <section className={styles.formSection}>
      <div className="container">
        <div className={styles.card}>
          <div className={styles.imgBox}>
            <p className={`s-fs-3 ${styles.imgBoxTitle}`}>
              {t("forms.signup_form.img_title")}
            </p>
            <img src={loginImg} alt="Sign up" className={styles.image} />
          </div>
          <div className={styles.textBox}>
            {success ? renderSuccessRegistration() : (
              <>
                <p className={`s-fs-3 ${styles.textBoxTitle}`}>
                  {t("forms.signup_form.form_title")}
                </p>
                <p className={`mb-4 small text-muted ${styles.textBoxDescription}`}>
                  {t("forms.signup_form.form_body")}
                </p>
                {renderSteps()}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
